import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { api } from '../../services/api';
import { Partner } from '../../types/api';

interface PendingGroupedByPartnerRow {
  id: number;
  partner_id: number;
  partner_name: string;
  item_id: number;
  order_id: number;
  pending_qty: number;
  previous_pending_qty: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface PendingGroupedByItemRow {
  id: number;
  partner_id: number;
  item_id: number;
  item_name: string;
  order_id: number;
  pending_qty: number;
  previous_pending_qty: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AvailableItem {
  id: number;
  name: string;
}

const PendingItemsReport: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [customers, setCustomers] = useState<Partner[]>([]);
  const [items, setItems] = useState<AvailableItem[]>([]);

  // By Partner state
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [groupedByItemData, setGroupedByItemData] = useState<PendingGroupedByItemRow[]>([]);
  const [partnerLoading, setPartnerLoading] = useState(false);
  
  // By Item state
  const [selectedItem, setSelectedItem] = useState<AvailableItem | null>(null);
  const [groupedByPartnerData, setGroupedByPartnerData] = useState<PendingGroupedByPartnerRow[]>([]);
  const [itemLoading, setItemLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/partners', { params: { type: 'CUSTOMER' } }),
      api.get('/api/items'),
    ]).then(([partnersRes, itemsRes]) => {
      setCustomers(partnersRes.data.partners || []);
      setItems((itemsRes.data.items || []).map((i: any) => ({ id: i.id, name: i.name })));
    }).catch((err) => console.error('Error fetching data:', err));
  }, []);

  const fetchGroupedByPartner = () => {
    setPartnerLoading(true);
    const params: any = {};
    if (selectedItem) params.item_id = selectedItem.id;
    api.get('/api/pending-items/by-partner', { params })
      .then((res) => setGroupedByPartnerData(res.data.pending_items || []))
      .catch(() => setGroupedByPartnerData([]))
      .finally(() => setPartnerLoading(false));
  };

  const fetchGroupedByItem = () => {
    setItemLoading(true);
    const params: any = {};
    if (selectedPartner) params.partner_id = selectedPartner.id;
    api.get('/api/pending-items/by-item', { params })
      .then((res) => setGroupedByItemData(res.data.pending_items || []))
      .catch(() => setGroupedByItemData([]))
      .finally(() => setItemLoading(false));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Pending Items Report
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Grouped By Item" />
        <Tab label="Grouped By Customer" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, maxWidth: 500 }}>
            <Autocomplete
              sx={{ flex: 1 }}
              options={customers}
              getOptionLabel={(o) => o.name}
              value={selectedPartner}
              onChange={(_, val) => setSelectedPartner(val)}
              renderInput={(params) => (
                <TextField {...params} label="Filter by Partner (optional)" size="small" />
              )}
              isOptionEqualToValue={(o, v) => o.id === v.id}
            />
            <Button variant="contained" startIcon={<Search />} onClick={fetchGroupedByItem} disabled={partnerLoading}>
              Search
            </Button>
          </Box>
          {partnerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : groupedByItemData.length === 0 ? (
            <Typography color="text.secondary">No pending items found.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Pending Qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedByItemData.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.item_name}</TableCell>
                      <TableCell align="right">{row.pending_qty}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, maxWidth: 500 }}>
            <Autocomplete
              sx={{ flex: 1 }}
              options={items}
              getOptionLabel={(o) => o.name}
              value={selectedItem}
              onChange={(_, val) => setSelectedItem(val)}
              renderInput={(params) => (
                <TextField {...params} label="Filter by Item (optional)" size="small" />
              )}
              isOptionEqualToValue={(o, v) => o.id === v.id}
            />
            <Button variant="contained" startIcon={<Search />} onClick={fetchGroupedByPartner} disabled={itemLoading}>
              Search
            </Button>
          </Box>
          {itemLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : groupedByPartnerData.length === 0 ? (
            <Typography color="text.secondary">No pending items found.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Pending Qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedByPartnerData.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.partner_name}</TableCell>
                      <TableCell align="right">{row.pending_qty}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PendingItemsReport;
